// src/components/DarkWebIntelTab.js
// Dark Web Intelligence tab — nested inside CyberNewsView
// Surfaces data from telegram_feed table (@breachdetect and future channels)

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  AlertTriangle, Globe, Clock, TrendingUp, Search, Filter,
  RefreshCw, Building2, Skull, Radio, ChevronDown, ChevronRight,
  ExternalLink, Shield, Zap, Eye, X
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const typeColor = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'ransomware': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'data leak':  return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'raw':        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    default:           return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
  }
};

const typeDot = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'ransomware': return 'bg-red-400';
    case 'data leak':  return 'bg-orange-400';
    default:           return 'bg-slate-500';
  }
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color = 'cyan', sub }) => (
  <div className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-5 h-5 text-${color}-400`} />
      <span className="text-2xl font-bold text-white">{value ?? '—'}</span>
    </div>
    <div className="text-xs text-slate-400">{label}</div>
    {sub && <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>}
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const DarkWebIntelTab = () => {
  const { getToken, isSignedIn } = useAuth();

  const [stats, setStats]       = useState(null);
  const [feed, setFeed]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);

  // Filters
  const [search, setSearch]         = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // ─── Fetch Stats ────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      const token = await getToken();
      const res = await fetch('/api/telegram-feed/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error('DarkWeb stats error:', e);
    }
  }, [isSignedIn, getToken]);

  // ─── Fetch Feed ─────────────────────────────────────────────────────────────
  const fetchFeed = useCallback(async () => {
    if (!isSignedIn) return;
    setFeedLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (search)       params.set('search', search);
      if (filterType)   params.set('type', filterType);
      if (filterSource) params.set('source', filterSource);

      const res = await fetch(`/api/telegram-feed?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      setFeed(data.rows || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.total || 0);
      if (data.rows?.length > 0 && !selected) setSelected(data.rows[0]);
    } catch (e) {
      setError(e.message);
    } finally {
      setFeedLoading(false);
      setLoading(false);
    }
  }, [isSignedIn, getToken, page, search, filterType, filterSource, selected]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchFeed(); }, [fetchFeed]);
  useEffect(() => { setPage(1); }, [search, filterType, filterSource]);

  const refresh = () => { fetchStats(); fetchFeed(); };

  if (loading && !stats && feed.length === 0) {
    // Initial skeleton
    return (
      <div className="flex-1 overflow-y-auto p-5 animate-pulse space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-20 bg-slate-800/40 rounded-xl" />)}
        </div>
        <div className="h-64 bg-slate-800/40 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">

      {/* ── Top Stats ─────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 p-5 border-b border-slate-800/40">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <StatCard icon={AlertTriangle} label="Total Detections"   value={stats?.total}         color="orange" />
          <StatCard icon={Clock}         label="Today"              value={stats?.today}          color="cyan"   />
          <StatCard icon={TrendingUp}    label="This Week"          value={stats?.this_week}      color="blue"   />
          <StatCard icon={Skull}         label="Ransomware Hits"    value={stats?.ransomware}     color="red"    sub="from @breachdetect" />
        </div>

        {/* Two-column insight panels */}
        <div className="grid grid-cols-2 gap-4">

          {/* Latest Breached Orgs */}
          <div className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-red-400" />
              Latest Breached Organizations
            </h3>
            <div className="space-y-2">
              {(stats?.latest_victims || []).length === 0 && (
                <p className="text-xs text-slate-500 italic">No named victims yet</p>
              )}
              {(stats?.latest_victims || []).map((v, i) => (
                <div key={i} className="flex items-center justify-between group">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                    <span className="text-xs text-slate-200 truncate">{v.victim}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${typeColor(v.post_type)}`}>
                      {v.post_type}
                    </span>
                    <span className="text-[10px] text-slate-500">{timeAgo(v.posted_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Sources */}
          <div className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-cyan-400" />
              Most Active Sources
            </h3>
            <div className="space-y-2">
              {(stats?.top_sources || []).map((s, i) => {
                const max = stats.top_sources[0]?.count || 1;
                return (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <button
                      onClick={() => setFilterSource(s.source)}
                      className="text-xs text-slate-300 hover:text-cyan-400 transition-colors truncate text-left"
                      title={s.source}
                    >
                      {s.source}
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-20 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-cyan-500/60 rounded-full"
                          style={{ width: `${Math.min(100, (s.count / max) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 w-8 text-right">{s.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Feed (split panel) ────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-[520px] overflow-hidden">

        {/* LEFT — list */}
        <div className="flex flex-col border-r border-slate-800/60 w-[420px] min-w-[300px]">

          {/* Filter bar */}
          <div className="flex-shrink-0 p-3 border-b border-slate-800/40 space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search content, victim, source..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-900/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
                />
              </div>
              <button
                onClick={refresh}
                className="p-1.5 rounded-lg bg-slate-800/50 text-slate-400 hover:text-cyan-400 border border-slate-700/40 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="flex-1 px-2 py-1 text-[11px] bg-slate-900/50 border border-slate-700/50 rounded text-slate-300"
              >
                <option value="">All Types</option>
                <option value="Data leak">Data Leak</option>
                <option value="ransomware">Ransomware</option>
                <option value="raw">Raw</option>
              </select>
              <select
                value={filterSource}
                onChange={e => setFilterSource(e.target.value)}
                className="flex-1 px-2 py-1 text-[11px] bg-slate-900/50 border border-slate-700/50 rounded text-slate-300"
              >
                <option value="">All Sources</option>
                {(stats?.top_sources || []).map((s, i) => (
                  <option key={i} value={s.source}>{s.source}</option>
                ))}
              </select>
              {(filterType || filterSource || search) && (
                <button
                  onClick={() => { setFilterType(''); setFilterSource(''); setSearch(''); }}
                  className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                  title="Clear filters"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="text-[10px] text-slate-500">{totalCount.toLocaleString()} detections</div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-800/30">
            {feedLoading && (
              <div className="p-4 space-y-2 animate-pulse">
                {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-slate-800/30 rounded" />)}
              </div>
            )}
            {!feedLoading && feed.map(row => (
              <button
                key={row.id}
                onClick={() => setSelected(row)}
                className={`w-full text-left px-3 py-2.5 hover:bg-slate-800/40 transition-colors ${
                  selected?.id === row.id ? 'bg-slate-800/50 border-l-2 border-cyan-500' : 'border-l-2 border-transparent'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${typeDot(row.post_type)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-200 line-clamp-2 leading-snug">
                      {row.victim
                        ? <span className="text-orange-400 font-medium">{row.victim} — </span>
                        : null}
                      {row.content || row.source}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-500 truncate">{row.source}</span>
                      <span className="text-[10px] text-slate-600">·</span>
                      <span className="text-[10px] text-slate-500">{timeAgo(row.posted_at)}</span>
                    </div>
                  </div>
                  <span className={`flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded border ${typeColor(row.post_type)}`}>
                    {row.post_type}
                  </span>
                </div>
              </button>
            ))}
            {!feedLoading && feed.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                <Radio className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-xs">No detections found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-t border-slate-800/40">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="text-[11px] text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-[11px] text-slate-500">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="text-[11px] text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* RIGHT — detail panel */}
        <div className="flex-1 overflow-y-auto bg-slate-950/50">
          {selected ? (
            <div className="p-6 max-w-2xl">

              {/* Type badge + time */}
              <div className="flex items-center gap-3 mb-4">
                <span className={`text-xs px-2.5 py-1 rounded border font-medium ${typeColor(selected.post_type)}`}>
                  {selected.post_type}
                </span>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {selected.posted_at ? new Date(selected.posted_at).toLocaleString() : ''}
                </span>
              </div>

              {/* Victim (if present) */}
              {selected.victim && (
                <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
                  <div className="text-[10px] text-red-400 font-medium mb-0.5 flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> Victim Organization
                  </div>
                  <div className="text-sm font-semibold text-white">{selected.victim}</div>
                </div>
              )}

              {/* Content */}
              <div className="mb-4 p-4 bg-slate-900/40 rounded-xl border border-slate-800/50">
                <div className="text-[10px] text-slate-500 font-medium mb-2 uppercase tracking-wider">Content</div>
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {selected.content || 'No content extracted.'}
                </p>
              </div>

              {/* Meta grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-slate-900/40 rounded-xl border border-slate-800/50">
                  <div className="text-[10px] text-slate-500 mb-1">Source</div>
                  <div className="text-xs text-slate-200 break-all">{selected.source || '—'}</div>
                </div>
                <div className="p-3 bg-slate-900/40 rounded-xl border border-slate-800/50">
                  <div className="text-[10px] text-slate-500 mb-1">Author / Handle</div>
                  <div className="text-xs text-slate-200">{selected.author || '—'}</div>
                </div>
                <div className="p-3 bg-slate-900/40 rounded-xl border border-slate-800/50">
                  <div className="text-[10px] text-slate-500 mb-1">Channel</div>
                  <div className="text-xs text-slate-200">@{selected.channel || 'breachdetect'}</div>
                </div>
                <div className="p-3 bg-slate-900/40 rounded-xl border border-slate-800/50">
                  <div className="text-[10px] text-slate-500 mb-1">Message ID</div>
                  <div className="text-xs text-slate-200 font-mono">{selected.message_id}</div>
                </div>
              </div>

              {/* Raw JSON (collapsible) */}
              {selected.raw_json && (
                <details className="mt-2">
                  <summary className="text-xs text-slate-500 hover:text-slate-300 cursor-pointer select-none flex items-center gap-1">
                    <ChevronRight className="w-3 h-3" />
                    Raw JSON
                  </summary>
                  <pre className="mt-2 p-3 bg-slate-900/60 rounded-lg border border-slate-800/40 text-[10px] text-slate-400 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(
                      typeof selected.raw_json === 'string' ? JSON.parse(selected.raw_json) : selected.raw_json,
                      null, 2
                    )}
                  </pre>
                </details>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <Eye className="w-10 h-10 mb-3 opacity-20" />
              <p className="text-sm">Select a detection to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DarkWebIntelTab;